/* WI-8 pending-claims advisory — pure data shaping (Ale ruling 2026-07-15).
 *
 * The proposal-group GET attaches `pending_claims` to every pending
 * update_* proposal: OTHER pending update proposals, from other
 * groups/documents, that claim the same (target row, field) — see
 * src/nolos/proposals/live_values.py::pending_claims_for. This module
 * folds that per-claim list into per-FIELD advisory rows the review
 * card can render as one passive line each:
 *
 *   "units held — 2 other pending documents claim this field:
 *    40,000 (subscription-a.pdf) / 42,500 (subscription-b.pdf)"
 *
 * Purely informational: never blocks or gates a confirm. Pure module,
 * no React — imported by components/review/EventCard and unit-tested
 * from tests/e2e/pending-claims-advisory.spec.ts.
 */

import { sameValue } from './approveDiff'

/** One backend advisory item — mirrors ProposalSummary.pending_claims[i]. */
export interface PendingClaim {
  proposal_id: string
  proposal_group_id: string
  proposal_type: string
  document_id: string | null
  document_filename: string | null
  /** Fund of the sibling group — lets the note deep-link to
   *  /funds/:fundId/events/:proposal_group_id (#842 round 6). */
  fund_id?: string | null
  fields: Record<string, unknown>
}

/** One other-document claim on a single field. */
export interface AdvisoryClaim {
  value: unknown
  documentFilename: string | null
  proposalGroupId: string
  /** Deep-link fund segment (see PendingClaim.fund_id). */
  fundId?: string | null
}

/** All other-document claims on one field, ready to render as one line. */
export interface FieldAdvisory {
  field: string
  claims: AdvisoryClaim[]
}

/** Scalar we can render; objects never make it into an advisory line. */
function renderable(v: unknown): boolean {
  return v != null && typeof v !== 'object'
}

/**
 * Regroup claim-per-proposal into advisory-per-field.
 *
 * - Field order follows first appearance across the claims (stable).
 * - Claims keep input order within a field.
 * - Duplicate readings collapse: the same value (value-level equality,
 *   so 40000 and "40000" are one reading) from the same document is
 *   listed once — two documents claiming the same value stay distinct.
 */
export function buildPendingClaimAdvisories(
  claims: PendingClaim[] | null | undefined
): FieldAdvisory[] {
  if (!claims || claims.length === 0) return []
  const byField = new Map<string, AdvisoryClaim[]>()
  for (const claim of claims) {
    for (const [field, value] of Object.entries(claim.fields ?? {})) {
      if (!renderable(value)) continue
      let rows = byField.get(field)
      if (!rows) {
        rows = []
        byField.set(field, rows)
      }
      const dup = rows.some(
        (r) => sameValue(r.value, value) && r.documentFilename === claim.document_filename
      )
      if (dup) continue
      rows.push({
        value,
        documentFilename: claim.document_filename,
        proposalGroupId: claim.proposal_group_id,
        fundId: claim.fund_id ?? null,
      })
    }
  }
  return [...byField.entries()].map(([field, rows]) => ({ field, claims: rows }))
}
